import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { cx, safeParse } from '@/utils';
import { useApolloClient } from '@apollo/client';
import { ButtonWithLogo } from '@common/ButtonWithLogo/ButtonWithLogo';
import Close from '@common/Icons/Close';
import SvgEye from '@common/Icons/Eye';
import Filter from '@common/Icons/Filter';
import Link2 from '@common/Icons/Link2';
import SvgRestore from '@common/Icons/Restore';
import SideArrowRight from '@common/Icons/SideArrowRight';
import SideArrowUp from '@common/Icons/SideArrowUp';
import Overlay from '@common/Overlay';
import {
  FindPageBlockByDynamicDocument,
  useGetComparativeJsonFromHistoryQuery,
  useHistoriesQuery,
  useRecoverPageDynamicMutation,
  type HistoriesQuery,
} from '@common/Providers/GraphqlProvider/generated';
import { DialogBody, Popover, PopoverContent, PopoverHandler } from '@material-tailwind/react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistoryStore, useModalDialogStore } from '@utils/store/store';
import { showErrorToast } from '@utils/toast/toastHelpers';
import { permissionsKeys } from '@views/Admin/private/ContentManagement/DynamicPages/PermissionsKey';
import {
  format,
  isSameMonth,
  isSameWeek,
  isToday,
  isYesterday,
  subMonths,
  subWeeks,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { capitalize } from 'lodash';
import moment from 'moment/moment';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { FaEllipsisV } from 'react-icons/fa';

import { Button } from '@/components/ui/Button';

const getBucketLabel = (d: Date | string) => {
  const now = new Date();
  const date = typeof d === 'string' ? new Date(d) : d;

  // Validate that date is valid
  if (isNaN(date.getTime())) {
    return 'Fecha no válida';
  }

  if (isToday(date)) return 'Hoy';
  if (isYesterday(date)) return 'Ayer';
  if (isSameWeek(date, now, { weekStartsOn: 1 })) return 'Esta semana';
  if (isSameWeek(date, subWeeks(now, 1), { weekStartsOn: 1 })) return 'La semana pasada';
  if (isSameMonth(date, subMonths(now, 1))) return 'El mes pasado';
  if (date.getFullYear() === now.getFullYear() - 1) return 'El año pasado';
  return capitalize(format(date, 'MMMM yyyy', { locale: es }));
};

type SidebarHistoryProps = {
  children?: React.ReactNode;
  sidebarContent?: React.ReactNode;
  pageDynamicId?: string; // Optional prop to override URL parameter extraction
};

const SidebarHistory: React.FC<SidebarHistoryProps> = ({
  children,
  sidebarContent,
  pageDynamicId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCompareVersionsModal, setShowCompareVersionsModal] = useState(false);
  const [baseOn, setBaseOn] = useState<string>();
  const [connector, setConnector] = useState<{ top: number; height: number } | null>(null);
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const handleConnect = (fromId: string, toId: string) => {
    const fromEl = itemRefs.current[fromId];
    const toEl = itemRefs.current[toId];
    if (fromEl && toEl) {
      const fromOffset = fromEl.offsetTop;
      const toOffset = toEl.offsetTop;
      setConnector({
        top: fromOffset + 30,
        height: toOffset - fromOffset,
      });
    }
  };
  const params = useParams();
  // Use provided pageDynamicId prop or try to extract from URL parameters
  const id = pageDynamicId || params.id || params.micrositeId || params.customSiteId;
  const { showVisible } = useModalDialogStore();
  const [recoverPageDynamic] = useRecoverPageDynamicMutation();
  const queryClient = useQueryClient();
  const { history, setHistory } = useHistoryStore();
  const client = useApolloClient();
  const { data, refetch } = useHistoriesQuery({
    fetchPolicy: 'no-cache',
    skip: !id || !isOpen,
    variables: {
      pageDynamicId: id as string,
      pagination: {
        skip: 0,
        take: 10,
      },
    },
  });

  useEffect(() => {
    return () => {
      setHistory({});
    };
  }, []);

  const handleRecoverPageDynamicVersion = (historyId: string, consecutive?: string | null) => {
    setShowCompareVersionsModal(false);
    showVisible({
      modalVisible: true,
      title: `¿Desea restaurar  la versión “${consecutive || historyId}” ?`,
      message:
        'Al hacerlo, esta versión reemplazará la actual y todos los cambios guardados hasta ahora se perderán.',
      type: 'info',
      confirmBtnLabel: 'Si, restaurar',
      onConfirm: async () => {
        try {
          await recoverPageDynamic({
            variables: {
              pageDynamicId: id as string,
              historyId,
            },
            refetchQueries: [
              {
                query: FindPageBlockByDynamicDocument,
                variables: { dynamicId: id as string },
                context: { headers: { 'x-permission-key': permissionsKeys.pageBlockFind } },
              },
            ],
          });
          await refetch();
          await client.refetchQueries({
            include: [FindPageBlockByDynamicDocument],
            updateCache: (cache) => {
              cache.evict({ fieldName: 'findPageBlockByDynamic' });
              cache.gc();
            },
          });
          await queryClient.invalidateQueries({ queryKey: ['PageDynamicById'] });
          showVisible({ modalVisible: false });
          setShowCompareVersionsModal(false);
          setHistory({});
        } catch (error) {
          showErrorToast(`Ha ocurrido un error al restaurar la versión “${id}”`);
        }
      },
    });
  };

  const actions = () => {
    return [
      {
        title: 'Restaurar esta versión',
        action: (id: string, consecutive?: string | null) =>
          handleRecoverPageDynamicVersion(id, consecutive),
      },
      {
        title: 'Comparar versiones',
        action: (id: string) => setShowCompareVersionsModal(true),
      },
    ];
  };

  const toggleSidebar = () => {
    setBaseOn(undefined);
    setConnector(null);
    setIsOpen(!isOpen);
  };

  const selectVersion = (history: HistoriesQuery['histories'][0]) => {
    setHistory({ history, pageDynamicId: id as string });
    setBaseOn(undefined);
    setConnector(null);
  };

  return (
    <>
      <div className="relative flex overflow-x-hidden">
        <div>
          <button onClick={toggleSidebar}>{children}</button>
        </div>

        <div
          className={`fixed right-0 top-[96px] z-50 h-full w-[24rem] transform border-l border-silver bg-white shadow-lg transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {sidebarContent || (
            <div className={'h-full overflow-y-auto px-3 py-3'}>
              <div className={'flex items-center justify-between'}>
                <div className={'flex items-center gap-2'}>
                  <div className={'headline-h4 text-primary'}>Historial</div>
                  <Filter width={15} height={15} className={'text-marine'} />
                </div>
                <button onClick={toggleSidebar}>
                  <Close width={15} height={15} className={'text-silver'} />
                </button>
              </div>
              <div className={'body-text1 text-tundora'}>
                Acá puede ver el todos los cambios de esta página en concreto
              </div>
              <div className={'mt-5 flex items-center gap-2'}>
                <div
                  className={
                    'flex h-[24px] w-[24px] items-center justify-center rounded-full bg-primaryLight'
                  }
                >
                  <SideArrowRight
                    strokeWidth={2.5}
                    className={`-rotate-90 text-primaryDark transition-transform`}
                  />
                </div>
                <div
                  className={
                    'font-workSans text-[16px] font-medium leading-[24px] text-primaryDark'
                  }
                >
                  Versiones guardadas
                </div>
              </div>
              <div className="relative ml-4 mt-5">
                {data?.histories &&
                  Object.entries(
                    data.histories.reduce(
                      (acc: Record<string, (typeof data)['histories']>, item) => {
                        const label = getBucketLabel(item.createdAt);
                        if (!acc[label]) acc[label] = [];
                        acc[label].push(item as any);
                        return acc;
                      },
                      {}
                    )
                  ).map(([label, list], i, array) => (
                    <div
                      key={label}
                      className={cx('border-gray-400 pb-1', {
                        '[&:not(:last-child)]:border-l': array.length > 1,
                        'border-l': array.length === 1,
                        '!border-l-0': baseOn,
                      })}
                    >
                      <div className="body-text2 relative mb-2 pl-6 text-tundora">
                        {!baseOn && (
                          <span className="absolute -left-[8px] top-0 h-4 w-4 rounded-full border border-gray-400 bg-gray-200" />
                        )}
                        {label}
                      </div>
                      {list.map((item, index) => (
                        <button
                          key={item.id}
                          className={cx(
                            'relative mb-6 ml-6 w-[319px] rounded-lg p-2 hover:bg-hawkesBlue',
                            {
                              ['border border-elfGreen bg-hawkesBlue']:
                                history?.id === item.id ||
                                (i === 0 && index === 0 && baseOn) ||
                                baseOn === item.id,
                            }
                          )}
                          onClick={i === 0 && index === 0 ? undefined : () => selectVersion(item)}
                          ref={(el) => (itemRefs.current[item.id] = el)}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={cx(
                                'text-description flex h-[44px] w-[44px] items-center justify-center rounded-full bg-primaryLight font-semibold text-primaryDark',
                                { ['bg-marineLight text-marine']: i === 0 && index === 0 && baseOn }
                              )}
                            >
                              {item?.changedBy?.name?.[0]}
                              {item?.changedBy?.lastName?.[0]}
                            </div>
                            <div className="flex flex-col">
                              <div className="headline-h7 flex items-center gap-2 text-left text-tundora">
                                {moment.utc(item.createdAt).format('hh:mm A')}
                                {i === 0 && index === 0 && (
                                  <>
                                    {item.basedOn?.id && (
                                      <button
                                        className={cx({ ['bg-marine']: baseOn }, 'rounded-full')}
                                        onClick={() => {
                                          if (baseOn) {
                                            setBaseOn(undefined);
                                            setConnector(null);
                                          } else if (item.basedOn?.id) {
                                            setBaseOn(item.basedOn.id);
                                            handleConnect(item.id, item.basedOn.id);
                                          }
                                          setHistory({});
                                        }}
                                      >
                                        <Link2
                                          className={cx('text-marine', { ['text-white']: baseOn })}
                                        />
                                      </button>
                                    )}
                                    <span className="text-blue-600">- Versión actual</span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center text-left text-sm text-gray-600">
                                <div
                                  className={
                                    'w-28 overflow-hidden text-ellipsis text-nowrap font-workSans text-[16px] font-normal text-dimGray'
                                  }
                                >
                                  {item?.changedBy?.name} {item?.changedBy?.lastName} -{' '}
                                </div>
                                <div
                                  className={
                                    'w-20 overflow-hidden text-ellipsis text-nowrap font-workSans text-[16px] font-normal text-silver'
                                  }
                                >
                                  {item.consecutive}
                                </div>
                              </div>
                            </div>
                            {!(i === 0 && index === 0) && (
                              <div className={'flex flex-1 justify-end'}>
                                <Popover placement="bottom-start">
                                  <PopoverHandler>
                                    <div
                                      className="cursor-pointer rounded p-1 hover:bg-marineLight"
                                      data-tooltip-id="msj_menu_action"
                                      data-tooltip-place="top"
                                    >
                                      <FaEllipsisV className="text-marine" />
                                    </div>
                                  </PopoverHandler>
                                  <PopoverContent className=" z-[50]  mr-2 p-0">
                                    {actions().map((x, index) => (
                                      <div
                                        key={index}
                                        onClick={() => x.action(item.id, item.consecutive)}
                                        className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-100 "
                                      >
                                        <div className="p-2 text-xs text-tundora">{x.title}</div>
                                      </div>
                                    ))}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                {connector && (
                  <>
                    <div
                      className="absolute border-t-2 border-dashed border-elfGreen"
                      style={{
                        left: 0,
                        top: connector.top,
                        width: 25,
                      }}
                    />
                    <div
                      className="absolute border-l-2 border-dashed border-elfGreen"
                      style={{
                        left: 0,
                        top: connector.top,
                        height: connector.height,
                      }}
                    />
                    <div
                      className="absolute border-t-2 border-dashed border-elfGreen"
                      style={{
                        left: 0,
                        top: connector.top + connector.height,
                        width: 25,
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={cx(
          'fixed left-1/2 -translate-x-1/2 gap-5 transition-all duration-500',
          history?.id ? 'bottom-10 opacity-100' : 'pointer-events-none -bottom-20 opacity-0',
          'z-10 flex items-center rounded-lg border border-silver bg-white px-5 py-2 shadow'
        )}
      >
        <div>
          <SvgEye width={22} height={15} className="text-marine" />
        </div>
        <div className={'flex items-center gap-2'}>
          <div className={'font-workSans text-[16px] font-normal text-dimGray'}>
            Viendo versión:
          </div>
          <div
            className={'headline-subtitle-2 w-auto rounded-lg bg-concrete p-1 px-2 text-tundora'}
          >
            {history?.consecutive}
          </div>
        </div>
        <div>
          <ButtonWithLogo
            onClick={() =>
              handleRecoverPageDynamicVersion(history?.id as string, history?.consecutive)
            }
            logo={<SvgRestore />}
            nameButton={'Restaurar'}
          />
        </div>
        <div>
          <button className={'text-primary'} onClick={() => setHistory({})}>
            Cerrar
          </button>
        </div>
      </div>
      {data?.histories.length ? (
        <CompareVersionsModal
          onOpenChange={() => setShowCompareVersionsModal(!showCompareVersionsModal)}
          open={showCompareVersionsModal}
          versionActual={data.histories[0]}
          handleRecoverPageDynamicVersion={handleRecoverPageDynamicVersion}
          histories={data.histories}
        />
      ) : null}
    </>
  );
};

export default SidebarHistory;

type CompareVersionsModalProps = {
  onOpenChange: () => void;
  open: boolean;
  versionActual: HistoriesQuery['histories'][0];
  handleRecoverPageDynamicVersion: (historyId: string, consecutive?: string | null) => void;
  histories: HistoriesQuery['histories'];
};

const CompareVersionsModal = ({
  onOpenChange,
  open,
  versionActual,
  handleRecoverPageDynamicVersion,
  histories,
}: CompareVersionsModalProps) => {
  const { history } = useHistoryStore();
  const [historySelected, setHistorySelected] = useState<HistoriesQuery['histories'][0]>();

  useEffect(() => {
    if (history?.id) {
      setHistorySelected(history);
    }
  }, [history?.id]);

  const { data } = useGetComparativeJsonFromHistoryQuery({
    skip: !history?.id || !versionActual?.id || !open,
    variables: {
      firstHistoryId: versionActual.id,
      secondHistoryId: historySelected?.id as string,
    },
  });

  return (
    <Overlay
      show={open}
      onClose={onOpenChange}
      title={'Comparar versiones'}
      description={'Acá puede comparar las versiones de la página'}
      onConfirm={() =>
        handleRecoverPageDynamicVersion(history?.id as string, historySelected?.consecutive)
      }
      confirmationText={'Reemplazar esta vesión'}
      className={'!min-w-[70em] transition-all'}
    >
      <div className="flex flex-row gap-6 py-3">
        <div className="flex flex-1 flex-col gap-2">
          <div className={'flex items-center gap-2'}>
            <div className={'font-workSans text-[14px] font-normal text-dimGray'}>Versión:</div>
            <div
              className={
                'headline-subtitle-2 w-[90px] overflow-hidden text-ellipsis text-nowrap rounded-2xl bg-concrete px-3 py-1 text-marine'
              }
            >
              {versionActual.consecutive}
            </div>
            <div className={'font-workSans text-[14px] font-normal text-marine'}>(Actual)</div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <div className={'flex items-center gap-2'}>
            <div className={'font-workSans text-[14px] font-normal text-dimGray'}>Versión:</div>
            <Popover placement="bottom-start">
              <PopoverHandler>
                <div
                  className={
                    'headline-subtitle-2 flex w-auto cursor-pointer items-center gap-2 overflow-hidden text-ellipsis text-nowrap rounded-2xl bg-concrete px-3 py-1 text-marine'
                  }
                >
                  {historySelected?.consecutive}
                  <SideArrowUp
                    strokeWidth={2.5}
                    className={cx(`text-primaryDark transition-transform`)}
                  />
                </div>
              </PopoverHandler>
              <PopoverContent className=" z-[9000000]  mr-2 p-0">
                {histories
                  .filter((e) => e.id !== versionActual.id)
                  .map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setHistorySelected(item)}
                      className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-100 "
                    >
                      {item.consecutive}
                    </div>
                  ))}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <div className="py-3">
        <div className="h-[400px] overflow-auto rounded border">
          <ReactDiffViewer
            oldValue={safeParse(data?.getComparativeJsonFromHistory?.[1])}
            newValue={safeParse(data?.getComparativeJsonFromHistory?.[0])}
            splitView={true}
            showDiffOnly={false}
            leftTitle="Versión comparada"
            rightTitle="Versión actual"
          />
        </div>
      </div>
    </Overlay>
  );
};
